import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';
import { Database, Layers, Tag, Plus, Server, Copy, Check, FileText } from 'lucide-react';
import Form from 'react-bootstrap/Form';
import DataAssetTreeView from '../components/DataAssetTreeView';
import { BIZ_SVG, FN_SVG, KPI_SVG } from '../utils/icons';
import { MAPPING_STATUS } from '../utils/constants';
import { customSelectStyles, makeIconComponents } from '../utils/selectStyles';
import {
    getConfigureBusinessMappingsPage,
    isConfigureMappingsPageSuccess,
    normalizeConfigureMappingsPageResponse
} from '../../../utils/configureMappingEventHandle';

const bizBadgeComponents = makeIconComponents(BIZ_SVG);
const fnBadgeComponents = makeIconComponents(FN_SVG);
const kpiBadgeComponents = makeIconComponents(KPI_SVG);
const envBadgeComponents = makeIconComponents(<Server size={16} />);

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const KEYWORDS_SET = new Set([
    'SUM', 'AVG', 'COUNT', 'MAX', 'MIN', 'ROUND', 'IF', 'CASE', 'WHEN', 'THEN', 'ELSE',
    'END', 'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'COALESCE',
    'CAST', 'CONCAT', 'ABS', 'CEIL', 'FLOOR', 'NULLIF', 'NVL', 'DISTINCT'
]);

const formatSavedFormula = (str) => {
    if (!str || typeof str !== 'string') return '';
    if (str.includes('\n')) return str;

    let formatted = str;
    formatted = formatted.replace(/\/\s*/g, '/\n');
    formatted = formatted.replace(/\b(FROM|WHERE|AND|OR|CASE|WHEN|THEN|ELSE|END)\b/gi, '\n$1');
    return formatted.split('\n').map(l => l.trim()).join('\n').trim();
};

const renderHighlightedCode = (text, ghostText = '', cursorIndex = null, meta = {}) => {
    if (!text && !ghostText) {
        return <span className="tw-text-gray-500 tw-italic">-- Write formula expression e.g. SUM(datasource.dataset.attribute) * 100</span>;
    }

    const { datasource, selectedDatasets } = meta;

    const dsSet = new Set();
    if (datasource) dsSet.add(String(datasource).toLowerCase());

    const datasetSet = new Set();
    if (selectedDatasets) {
        const arr = selectedDatasets instanceof Set ? Array.from(selectedDatasets) : (Array.isArray(selectedDatasets) ? selectedDatasets : []);
        arr.forEach(d => d && datasetSet.add(String(d).toLowerCase()));
    }

    const displayText = text || '';
    const lines = displayText.split('\n');

    let cursorLineIdx = lines.length - 1;
    let cursorColIdx = lines[lines.length - 1].length;

    if (cursorIndex !== null && cursorIndex !== undefined) {
        let charCount = 0;
        for (let l = 0; l < lines.length; l++) {
            const lineLen = lines[l].length;
            if (cursorIndex >= charCount && cursorIndex <= charCount + lineLen) {
                cursorLineIdx = l;
                cursorColIdx = cursorIndex - charCount;
                break;
            }
            charCount += lineLen + 1;
        }
    }

    return lines.map((line, lineIdx) => {
        const isCursorLine = lineIdx === cursorLineIdx;
        const tokens = [];
        let index = 0;
        const regex = /(--.*|\/\/.*)|(["'].*?["'])|(\b[a-zA-Z_][a-zA-Z0-9_-]*\.[a-zA-Z_][a-zA-Z0-9_-]*\.[a-zA-Z_][a-zA-Z0-9_-]*\b)|(\b[a-zA-Z_][a-zA-Z0-9_-]*\.[a-zA-Z_][a-zA-Z0-9_-]*\b)|(\b[a-zA-Z_][a-zA-Z0-9_-]*\b)|(\b\d+(?:\.\d+)?\b)|(\+|-|\*|\/|=|>|<|>=|<=|!=|%)|(\(|\)|,|;)/g;
        let match;

        while ((match = regex.exec(line)) !== null) {
            if (match.index > index) {
                tokens.push({ text: line.slice(index, match.index), type: 'plain' });
            }

            const [, comment, str, tripleDot, doubleDot, word, num, op, punc] = match;

            if (comment) tokens.push({ text: comment, type: 'comment' });
            else if (str) tokens.push({ text: str, type: 'string' });
            else if (tripleDot) {
                const parts = tripleDot.split('.');
                tokens.push({ text: parts[0], type: 'datasource' });
                tokens.push({ text: '.', type: 'punc' });
                tokens.push({ text: parts[1], type: 'dataset' });
                tokens.push({ text: '.', type: 'punc' });
                tokens.push({ text: parts[2], type: 'attribute' });
            } else if (doubleDot) {
                const parts = doubleDot.split('.');
                const isFirstDs = dsSet.has(parts[0].toLowerCase()) || (datasource && parts[0].toLowerCase() === String(datasource).toLowerCase());
                tokens.push({ text: parts[0], type: isFirstDs ? 'datasource' : 'dataset' });
                tokens.push({ text: '.', type: 'punc' });
                tokens.push({ text: parts[1], type: isFirstDs ? 'dataset' : 'attribute' });
            } else if (word) {
                if (KEYWORDS_SET.has(word.toUpperCase())) {
                    tokens.push({ text: word, type: 'keyword' });
                } else if (dsSet.has(word.toLowerCase())) {
                    tokens.push({ text: word, type: 'datasource' });
                } else if (datasetSet.has(word.toLowerCase())) {
                    tokens.push({ text: word, type: 'dataset' });
                } else {
                    tokens.push({ text: word, type: 'attribute' });
                }
            } else if (num) tokens.push({ text: num, type: 'number' });
            else if (op) tokens.push({ text: op, type: 'operator' });
            else if (punc) tokens.push({ text: punc, type: 'punc' });

            index = regex.lastIndex;
        }

        if (index < line.length) {
            tokens.push({ text: line.slice(index), type: 'plain' });
        }

        let currentPos = 0;
        let ghostInserted = false;
        const renderedTokens = [];

        if (tokens.length === 0) {
            if (isCursorLine && ghostText) {
                return (
                    <div key={lineIdx} className="tw-min-h-[24px] tw-leading-6">
                        <span className="tw-text-gray-400/60 tw-italic tw-select-none tw-pointer-events-none">{ghostText}</span>
                    </div>
                );
            }
            return <div key={lineIdx} className="tw-min-h-[24px] tw-leading-6">{'\u00A0'}</div>;
        }

        tokens.forEach((tok, tokIdx) => {
            let colorClass = 'tw-text-white';
            if (tok.type === 'keyword') colorClass = 'tw-text-[#569CD6] tw-font-bold';
            else if (tok.type === 'datasource') colorClass = 'tw-text-[#E06C75] tw-font-bold';
            else if (tok.type === 'dataset') colorClass = 'tw-text-[#38BDF8] tw-font-semibold';
            else if (tok.type === 'attribute') colorClass = 'tw-text-[#34D399] tw-font-medium';
            else if (tok.type === 'number') colorClass = 'tw-text-[#B5CEA8]';
            else if (tok.type === 'string') colorClass = 'tw-text-[#CE9178]';
            else if (tok.type === 'comment') colorClass = 'tw-text-[#6A9955] tw-italic';
            else if (tok.type === 'operator') colorClass = 'tw-text-[#56B6C2] tw-font-bold';
            else if (tok.type === 'punc') colorClass = 'tw-text-[#FFD700]';

            const tokStart = currentPos;
            const tokEnd = currentPos + tok.text.length;
            currentPos = tokEnd;

            if (isCursorLine && !ghostInserted && ghostText && cursorColIdx >= tokStart && cursorColIdx <= tokEnd) {
                const splitAt = cursorColIdx - tokStart;
                const part1 = tok.text.slice(0, splitAt);
                const part2 = tok.text.slice(splitAt);

                if (part1) {
                    renderedTokens.push(
                        <span key={`${tokIdx}-a`} className={colorClass}>
                            {part1}
                        </span>
                    );
                }
                renderedTokens.push(
                    <span key={`${tokIdx}-ghost`} className="tw-text-gray-400/60 tw-italic tw-select-none tw-pointer-events-none">
                        {ghostText}
                    </span>
                );
                if (part2) {
                    renderedTokens.push(
                        <span key={`${tokIdx}-b`} className={colorClass}>
                            {part2}
                        </span>
                    );
                }
                ghostInserted = true;
            } else {
                renderedTokens.push(
                    <span key={tokIdx} className={colorClass}>
                        {tok.text}
                    </span>
                );
            }
        });

        if (isCursorLine && !ghostInserted && ghostText) {
            renderedTokens.push(
                <span key="end-ghost" className="tw-text-gray-400/60 tw-italic tw-select-none tw-pointer-events-none">
                    {ghostText}
                </span>
            );
        }

        return (
            <div key={lineIdx} className="tw-min-h-[24px] tw-leading-6">
                {renderedTokens}
            </div>
        );
    });
};

class CodeMirrorFormulaBox extends React.Component {
    state = {
        copied: false,
        showDropdown: false,
        filterText: '',
        cursorIndex: 0,
        selectedIndex: 0,
        dropdownTop: 10,
        dropdownLeft: 10
    };

    textareaRef = React.createRef();
    highlightRef = React.createRef();

    handleCopy = () => {
        if (this.props.value) {
            navigator.clipboard.writeText(this.props.value);
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        }
    };

    handleScroll = () => {
        if (this.textareaRef.current && this.highlightRef.current) {
            this.highlightRef.current.scrollTop = this.textareaRef.current.scrollTop;
            this.highlightRef.current.scrollLeft = this.textareaRef.current.scrollLeft;
        }
    };

    getSuggestions = () => {
        const { datasource, selectedDatasets, selectedAttributes, assets } = this.props;
        const suggestions = [];

        // 1. Selected Datasource
        if (datasource) {
            suggestions.push({ label: datasource, type: 'Datasource', color: '#E06C75', icon: Database });
        }

        // 2. Selected Datasets
        if (selectedDatasets) {
            const dsSet = selectedDatasets instanceof Set ? Array.from(selectedDatasets) : (Array.isArray(selectedDatasets) ? selectedDatasets : []);
            dsSet.forEach(dsName => {
                if (dsName && !suggestions.some(s => s.label === dsName)) {
                    suggestions.push({ label: dsName, type: 'Dataset', color: '#38BDF8', icon: Layers });
                }
            });
        }

        // 3. Selected Attributes / Assets
        if (assets && Array.isArray(assets)) {
            const attrSet = selectedAttributes instanceof Set ? selectedAttributes : new Set(Array.isArray(selectedAttributes) ? selectedAttributes : []);
            assets.forEach(a => {
                const isSelected = attrSet.has(a.id) || attrSet.has(a.name) || attrSet.has(a.attribute);
                const attrName = a.attribute || a.name || a.column || (typeof a === 'string' ? a : '');
                if (isSelected && attrName && !suggestions.some(s => s.label === attrName)) {
                    suggestions.push({ label: attrName, type: 'Attribute', color: '#34D399', icon: Tag });
                }
            });
        }

        return suggestions;
    };

    getGhostText = (value, cursorIndex) => {
        const textBeforeCursor = (value || '').slice(0, cursorIndex || 0);
        const { datasource, selectedDatasets, selectedAttributes, assets } = this.props;

        const dsList = selectedDatasets instanceof Set ? Array.from(selectedDatasets) : (Array.isArray(selectedDatasets) ? selectedDatasets : []);
        const attrList = [];
        if (assets && Array.isArray(assets)) {
            const attrSet = selectedAttributes instanceof Set ? selectedAttributes : new Set(Array.isArray(selectedAttributes) ? selectedAttributes : []);
            assets.forEach(a => {
                const isSelected = attrSet.size === 0 || attrSet.has(a.id) || attrSet.has(a.name) || attrSet.has(a.attribute);
                const attrName = a.attribute || a.name || a.column || (typeof a === 'string' ? a : '');
                if (isSelected && attrName && !attrList.includes(attrName)) {
                    attrList.push(attrName);
                }
            });
        }

        const firstAttr = attrList[0] || 'attribute';
        const secondAttr = attrList[1] || firstAttr;
        const firstDs = dsList[0] || 'dataset';

        const match = textBeforeCursor.match(/([a-zA-Z0-9_.]+)$/);
        const currentWord = match ? match[1] : '';
        const textBeforeWord = textBeforeCursor.slice(0, textBeforeCursor.length - currentWord.length);
        const trimmedBefore = textBeforeWord.trimEnd();
        const upperBefore = trimmedBefore.toUpperCase();

        // 1. SQL Keyword & Function Ghost Text Autocomplete (when typing a keyword/function)
        if (currentWord && currentWord.length >= 1) {
            const keywordsAndFunctions = [
                { name: 'SELECT', suffix: '' },
                { name: 'FROM', suffix: '' },
                { name: 'WHERE', suffix: '' },
                { name: 'SUM', suffix: '()' },
                { name: 'AVG', suffix: '()' },
                { name: 'COUNT', suffix: '()' },
                { name: 'MAX', suffix: '()' },
                { name: 'MIN', suffix: '()' },
                { name: 'ROUND', suffix: '()' },
                { name: 'IF', suffix: '()' },
                { name: 'CASE', suffix: '' },
                { name: 'COALESCE', suffix: '()' },
                { name: 'NULLIF', suffix: '()' },
                { name: 'AND', suffix: '' },
                { name: 'OR', suffix: '' }
            ];

            const upperWord = currentWord.toUpperCase();
            const found = keywordsAndFunctions.find(item =>
                item.name.startsWith(upperWord) && item.name.length > upperWord.length
            );

            if (found) {
                const remainingKeyword = found.name.slice(upperWord.length);
                return remainingKeyword + found.suffix;
            }
        }

        // 2. Clause/Context Completion Suffix (based on text before current word)
        if (upperBefore.endsWith('SELECT')) {
            if (attrList.length >= 2) {
                return ` (SUM(${firstAttr}) - SUM(${secondAttr})) / NULLIF(SUM(${firstAttr}), 0) * 100`;
            } else if (attrList.length === 1) {
                return ` SUM(${firstAttr})`;
            }
            return ' *';
        }

        if (upperBefore.endsWith('FROM')) {
            if (datasource && firstDs) {
                return ` ${datasource}.${firstDs}`;
            } else if (firstDs) {
                return ` ${firstDs}`;
            }
        }

        if (upperBefore.endsWith('WHERE')) {
            return ` ${firstAttr} IS NOT NULL`;
        }

        if (upperBefore.endsWith('AND') || upperBefore.endsWith('OR')) {
            return ` ${secondAttr} IS NOT NULL`;
        }

        if (upperBefore.endsWith('CASE')) {
            return ` WHEN ${firstAttr} IS NOT NULL THEN ${firstAttr} ELSE NULL END`;
        }

        return '';
    };

    handleTextareaChange = (e) => {
        const text = e.target.value;
        const cursor = e.target.selectionStart;
        this.props.onChange(text);

        const textBeforeCursor = text.slice(0, cursor);
        const match = textBeforeCursor.match(/([a-zA-Z0-9_.]+)$/);
        const currentWord = match ? match[1] : '';
        const matchingSuggestions = currentWord ? this.getFilteredSuggestions(currentWord) : [];

        if (currentWord && currentWord.length >= 1 && matchingSuggestions.length > 0) {
            const linesBefore = textBeforeCursor.split('\n');
            const currentLine = linesBefore.length;
            const currentLineText = linesBefore[linesBefore.length - 1];
            const colStart = Math.max(0, currentLineText.length - currentWord.length);

            const lineTop = 10 + (currentLine - 1) * 24;
            const leftOffset = Math.max(10, Math.min(10 + colStart * 7.2, 180));

            this.setState({
                showDropdown: true,
                filterText: currentWord,
                cursorIndex: cursor,
                selectedIndex: 0,
                dropdownTop: lineTop,
                dropdownLeft: leftOffset
            });
        } else {
            this.setState({ showDropdown: false, filterText: currentWord, cursorIndex: cursor });
        }
    };

    updateCursor = (e) => {
        if (e.target && typeof e.target.selectionStart === 'number') {
            this.setState({ cursorIndex: e.target.selectionStart });
        }
    };

    insertSuggestion = (suggestionLabel) => {
        const { value, onChange } = this.props;
        const { filterText, cursorIndex } = this.state;

        let textBeforeWord = value || '';
        let textAfterWord = '';
        const currentCursor = this.textareaRef.current ? this.textareaRef.current.selectionStart : textBeforeWord.length;

        if (filterText && cursorIndex > 0) {
            textBeforeWord = (value || '').slice(0, cursorIndex - filterText.length);
            textAfterWord = (value || '').slice(cursorIndex);
        } else {
            textBeforeWord = (value || '').slice(0, currentCursor);
            textAfterWord = (value || '').slice(currentCursor);
        }

        const isFunc = ['SUM', 'AVG', 'COUNT', 'MAX', 'MIN', 'ROUND', 'IF', 'COALESCE'].includes(suggestionLabel);
        const insertText = isFunc ? `${suggestionLabel}()` : suggestionLabel;
        let newCursorPos = textBeforeWord.length + insertText.length;
        if (isFunc) {
            newCursorPos -= 1;
        }

        const newValue = textBeforeWord + insertText + textAfterWord;
        onChange(newValue);

        this.setState({ showDropdown: false, filterText: '', cursorIndex: newCursorPos }, () => {
            if (this.textareaRef.current) {
                this.textareaRef.current.focus();
                this.textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        });
    };

    getFilteredSuggestions = (filterText) => {
        if (!filterText) return [];
        const suggestions = this.getSuggestions();
        return suggestions.filter(s => s.label.toLowerCase().startsWith(filterText.toLowerCase()));
    };

    handleKeyDown = (e) => {
        const { showDropdown, selectedIndex, filterText, cursorIndex } = this.state;
        const { value, onChange } = this.props;
        const filtered = this.getFilteredSuggestions(filterText);
        const ghostText = this.getGhostText(value, cursorIndex);

        if (e.key === 'Tab') {
            if (showDropdown && filtered.length > 0 && filtered[selectedIndex]) {
                e.preventDefault();
                this.insertSuggestion(filtered[selectedIndex].label);
                return;
            } else if (ghostText) {
                e.preventDefault();
                let textBefore = (value || '').slice(0, cursorIndex);
                const textAfter = (value || '').slice(cursorIndex);

                const match = textBefore.match(/([a-zA-Z0-9_.]+)$/);
                const currentWord = match ? match[1] : '';

                if (currentWord) {
                    const textBeforeWord = textBefore.slice(0, textBefore.length - currentWord.length);
                    const upperWord = currentWord.toUpperCase();
                    const isKeywordOrFn = [
                        'SELECT', 'FROM', 'WHERE', 'SUM', 'AVG', 'COUNT', 'MAX', 'MIN',
                        'ROUND', 'IF', 'CASE', 'COALESCE', 'NULLIF', 'AND', 'OR', 'WHEN', 'THEN', 'ELSE', 'END'
                    ].some(k => k.startsWith(upperWord));

                    if (isKeywordOrFn) {
                        textBefore = textBeforeWord + upperWord;
                    }
                } else {
                    textBefore = textBefore.replace(/\b(select|from|where|and|or|case|when|then|else|end|sum|avg|count|max|min|round|if|coalesce|nullif)\b(?=\s*$)/gi, (m) => m.toUpperCase());
                }

                const newValue = textBefore + ghostText + textAfter;
                let newCursorPos = textBefore.length + ghostText.length;

                if (ghostText.endsWith('()')) {
                    newCursorPos -= 1;
                }

                onChange(newValue);
                this.setState({ showDropdown: false, filterText: '', cursorIndex: newCursorPos }, () => {
                    if (this.textareaRef.current) {
                        this.textareaRef.current.focus();
                        this.textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                    }
                });
                return;
            }
        }

        if (showDropdown && filtered.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                this.setState({ selectedIndex: (selectedIndex + 1) % filtered.length });
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                this.setState({ selectedIndex: (selectedIndex - 1 + filtered.length) % filtered.length });
            } else if (e.key === 'Enter') {
                if (filtered[selectedIndex]) {
                    e.preventDefault();
                    this.insertSuggestion(filtered[selectedIndex].label);
                }
            } else if (e.key === 'Escape') {
                this.setState({ showDropdown: false });
            }
        }
    };

    render() {
        const { value } = this.props;
        const { copied, showDropdown, filterText, selectedIndex, dropdownTop, dropdownLeft, cursorIndex } = this.state;
        const lines = (value || '').split('\n');
        const lineCount = Math.max(lines.length, 3);
        const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1);

        const filtered = this.getFilteredSuggestions(filterText);
        const ghostText = this.getGhostText(value, cursorIndex);

        return (
            <div className="tw-rounded-lg tw-bg-[#0C0E12] tw-shadow-sm !tw-border-0 font-mono tw-relative">
                {/* Header Bar with Tab Autocomplete Hint & Copy Icon */}
                <div className="tw-flex tw-items-center tw-justify-end tw-px-3 tw-py-1.5 tw-bg-[#0C0E12] tw-border-0 tw-border-b tw-border-solid tw-border-white/10 tw-rounded-t-lg">
                    <button
                        type="button"
                        onClick={this.handleCopy}
                        title="Copy formula"
                        className="tw-text-gray-400 hover:tw-text-white tw-transition-colors tw-bg-transparent tw-border-0 tw-p-1 tw-rounded tw-cursor-pointer tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-shrink-0"
                    >
                        {copied ? <Check size={12} className="tw-text-emerald-400" /> : <Copy size={12} />}
                        <span>{copied ? 'Copied' : 'Copy'}</span>
                    </button>
                </div>

                {/* Editor Body */}
                <div className="!tw-flex tw-min-h-[92px] tw-bg-[#0C0E12] tw-relative tw-rounded-b-lg">
                    {/* Left Line Numbers Panel */}
                    <div className="tw-select-none tw-py-2.5 tw-px-2.5 tw-bg-[#0C0E12] tw-text-[#525866] tw-text-xs tw-font-mono tw-text-right tw-min-w-[34px] tw-leading-6 tw-border-0 tw-border-r tw-border-solid tw-border-white/10 tw-z-10 tw-rounded-bl-lg">
                        {lineNumbers.map((num) => (
                            <div key={num}>{num}</div>
                        ))}
                    </div>

                    {/* Text Input Area & Syntax Highlight Overlay */}
                    <div className="tw-flex-1 tw-relative tw-min-h-[92px]">
                        {/* Syntax Highlighted Overlay with Inline Ghost Text */}
                        <div
                            ref={this.highlightRef}
                            aria-hidden="true"
                            className="tw-absolute tw-inset-0 tw-p-2.5 tw-text-xs tw-font-mono tw-leading-6 tw-whitespace-pre tw-overflow-hidden tw-pointer-events-none"
                        >
                            {renderHighlightedCode(value, ghostText, cursorIndex, this.props)}
                        </div>

                        {/* Transparent Textarea for typing with autocomplete */}
                        <textarea
                            ref={this.textareaRef}
                            value={value}
                            onChange={this.handleTextareaChange}
                            onKeyDown={this.handleKeyDown}
                            onClick={this.updateCursor}
                            onKeyUp={this.updateCursor}
                            onSelect={this.updateCursor}
                            onScroll={this.handleScroll}
                            onBlur={() => setTimeout(() => this.setState({ showDropdown: false }), 200)}
                            placeholder="-- Write formula expression e.g. SUM(datasource.dataset.attribute) * 100"
                            rows={Math.max(lines.length, 3)}
                            spellCheck={false}
                            autoCorrect="off"
                            autoCapitalize="off"
                            className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-p-2.5 tw-bg-transparent tw-text-xs tw-font-mono tw-text-transparent tw-caret-white tw-outline-none tw-resize-y tw-leading-6 placeholder:tw-text-gray-500 !tw-border-0 focus:!tw-ring-0"
                        />

                        {/* Autocomplete Suggestions Dropdown Popup */}
                        {showDropdown && filtered.length > 0 && (
                            <div
                                className="tw-absolute tw-z-50 tw-w-64 tw-bg-[#181A20] tw-border tw-border-solid tw-border-white/15 tw-rounded-lg tw-shadow-2xl tw-py-1 tw--translate-y-full tw--mb-1 tw-transition-all tw-duration-75"
                                style={{
                                    top: `${dropdownTop ?? 10}px`,
                                    left: `${dropdownLeft ?? 10}px`
                                }}
                            >
                                <div className="tw-max-h-[96px] tw-overflow-y-auto scr">
                                    {filtered.map((s, idx) => {
                                        const ItemIcon = s.icon;
                                        return (
                                            <div
                                                key={idx}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    this.insertSuggestion(s.label);
                                                }}
                                                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-gap-2 tw-transition-colors ${idx === selectedIndex ? 'tw-bg-white/15 tw-text-white' : 'hover:tw-bg-white/10 tw-text-gray-200'}`}
                                            >
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-1" title={s.label}>
                                                    {ItemIcon && <ItemIcon size={13} style={{ color: s.color }} className="tw-shrink-0" />}
                                                    <span style={{ color: s.color }} className="tw-font-medium tw-truncate">
                                                        {s.label}
                                                    </span>
                                                </div>
                                                <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-white/10 tw-text-gray-300 font-sans tw-shrink-0">
                                                    {s.type}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

class AddMappingModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = this.getInitialFormState();
    }

    getInitialFormState() {
        return {
            envId: '',
            bizId: '',
            fnId: '',
            kpiId: '',
            description: '',
            datasource: '',
            selectedDatasets: new Set(),
            selectedAttributes: new Set(),
            formulaValue: '',
            errors: {},
            submitted: false,
            envAssets: null,
            envAssetsLoading: false
        };
    }

    componentDidUpdate(prevProps) {
        const opened = this.props.show && !prevProps.show;
        const mappingChanged = this.props.editingMapping !== prevProps.editingMapping;

        if (opened || (this.props.show && mappingChanged)) {
            const formState = this.props.editingMapping
                ? this.buildEditFormState(this.props.editingMapping)
                : this.getInitialFormState();
            this.setState(formState, () => {
                const envId = formState.envId || this.getPageEnvironmentId();
                if (envId) this.loadAssetsForEnvironment(envId);
            });
        }
    }

    getPageEnvironmentId() {
        const { filterEnvironment } = this.props;
        if (filterEnvironment && filterEnvironment !== 'all') return String(filterEnvironment);
        return '';
    }

    getSelectedEnvironmentId() {
        if (this.state.envId) return String(this.state.envId);
        return this.getPageEnvironmentId();
    }

    filterAssetsForEnvironment(assetList, envId) {
        const list = Array.isArray(assetList) ? assetList : [];
        if (!envId) return list;
        const tagged = list.some((asset) => asset && asset.env_id != null && asset.env_id !== '');
        if (!tagged) return list;
        return list.filter((asset) => String(asset.env_id) === String(envId));
    }

    getVisibleAssets() {
        if (Array.isArray(this.state.envAssets)) return this.state.envAssets;
        return this.filterAssetsForEnvironment(this.props.assets, this.getSelectedEnvironmentId());
    }

    loadAssetsForEnvironment = (envId) => {
        const pageEnv = this.getPageEnvironmentId();
        if (!envId || (pageEnv && String(pageEnv) === String(envId))) {
            this._assetsRequestId = (this._assetsRequestId || 0) + 1;
            this.setState({ envAssets: null, envAssetsLoading: false });
            return;
        }

        const requestId = (this._assetsRequestId || 0) + 1;
        this._assetsRequestId = requestId;
        this.setState({ envAssets: [], envAssetsLoading: true });

        getConfigureBusinessMappingsPage({
            tenant_id: localStorage.getItem('team_id'),
            user_id: localStorage.getItem('user_id'),
            env_id: String(envId)
        }).then((response) => {
            if (this._assetsRequestId !== requestId || !this.props.show) return;
            const page = isConfigureMappingsPageSuccess(response)
                ? normalizeConfigureMappingsPageResponse(response)
                : { assets: [] };
            this.setState({
                envAssets: page.assets || [],
                envAssetsLoading: false
            });
        }).catch(() => {
            if (this._assetsRequestId !== requestId) return;
            this.setState({ envAssets: [], envAssetsLoading: false });
        });
    };

    buildEditFormState(editingMapping) {
        const { kpis, assets, mappings = [], functions } = this.props;
        const m = editingMapping;

        let kpiId = m.kpiId ?? m.kpi?.id ?? null;
        if (kpiId == null && Array.isArray(m.mappingIds) && m.mappingIds.length) {
            const firstMapping = mappings.find((item) => m.mappingIds.some((id) => sameId(id, item.id)));
            kpiId = firstMapping?.kpiId ?? null;
        }
        if (kpiId == null && m.kpiName) {
            const byName = kpis.find((k) => k.name === m.kpiName);
            kpiId = byName?.id ?? null;
        }

        const k = kpis.find((item) => sameId(item.id, kpiId));
        const fnId = k?.functionId ?? m.functionId ?? m.fn?.id ?? '';
        const fn = functions.find((item) => sameId(item.id, fnId));
        const bizId = m.businessId ?? m.biz?.id ?? fn?.businessId ?? '';
        const envId = m.environment || m.env || m.env_id || '';

        let relatedAssets = [];
        if (Array.isArray(m.mappedAssets) && m.mappedAssets.length) {
            relatedAssets = m.mappedAssets;
        } else {
            const relatedMappings = mappings.filter((item) => sameId(item.kpiId, kpiId));
            const relatedAssetIds = relatedMappings.length
                ? relatedMappings.map((item) => item.assetId)
                : (m.assetId ? [m.assetId] : []);
            relatedAssets = relatedAssetIds
                .map((id) => assets.find((a) => sameId(a.id, id)))
                .filter(Boolean);
        }

        const dsCounts = {};
        relatedAssets.forEach((a) => {
            if (a.datasource) {
                dsCounts[a.datasource] = (dsCounts[a.datasource] || 0) + 1;
            }
        });
        const datasource = (Array.isArray(m.datasources) && m.datasources[0])
            || Object.keys(dsCounts).sort((a, b) => dsCounts[b] - dsCounts[a])[0]
            || (relatedAssets[0] && relatedAssets[0].datasource)
            || '';

        return {
            envId,
            bizId,
            fnId,
            kpiId: k ? k.id : (kpiId ?? ''),
            description: k?.description || editingMapping.kpiDescription || editingMapping.description || '',
            datasource,
            selectedDatasets: new Set(relatedAssets.map((a) => a.dataset).filter(Boolean)),
            selectedAttributes: new Set(relatedAssets.map((a) => a.id)),
            formulaValue: formatSavedFormula(m.formula ?? (k ? (k.formula || '') : '')),
            errors: {},
            submitted: false,
            envAssets: null,
            envAssetsLoading: false
        };
    }

    getBizIdFromFn(fnId) {
        const { functions } = this.props;
        const f = functions.find((item) => sameId(item.id, fnId));
        return f ? f.businessId : '';
    }

    onFieldChange = (field, val) => {
        const nextState = { [field]: val };
        const clearedErrors = { ...this.state.errors };

        if (field === 'bizId') {
            nextState.fnId = '';
            nextState.kpiId = '';
            nextState.description = '';
            nextState.formulaValue = '';
            clearedErrors.bizId = '';
            clearedErrors.fnId = '';
            clearedErrors.kpiId = '';
        } else if (field === 'fnId') {
            nextState.kpiId = '';
            nextState.description = '';
            nextState.formulaValue = '';
            clearedErrors.fnId = '';
            clearedErrors.kpiId = '';
        } else if (field === 'kpiId') {
            const selectedKpi = this.props.kpis.find((k) => sameId(k.id, val));
            nextState.formulaValue = selectedKpi ? formatSavedFormula(selectedKpi.formula || '') : '';
            nextState.description = selectedKpi ? (selectedKpi.description || '') : '';
            clearedErrors.kpiId = '';
        } else if (field === 'datasource') {
            nextState.selectedDatasets = new Set();
            nextState.selectedAttributes = new Set();
            clearedErrors.datasource = '';
            clearedErrors.attributes = '';
        } else if (field === 'envId') {
            const pageEnv = this.getPageEnvironmentId();
            const switchingAway = val && (!pageEnv || String(val) !== String(pageEnv));
            nextState.datasource = '';
            nextState.selectedDatasets = new Set();
            nextState.selectedAttributes = new Set();
            nextState.envAssets = switchingAway ? [] : null;
            nextState.envAssetsLoading = switchingAway;
            clearedErrors.datasource = '';
            clearedErrors.attributes = '';
        }

        if (clearedErrors[field] !== undefined) {
            clearedErrors[field] = '';
        }

        this.setState({ ...nextState, errors: clearedErrors }, () => {
            if (field === 'envId') this.loadAssetsForEnvironment(val);
        });
    };

    validateForm = () => {
        const { bizId, fnId, kpiId, datasource, selectedAttributes } = this.state;
        const errors = {};

        if (!bizId) errors.bizId = 'Business is required';
        if (!fnId) errors.fnId = 'Function is required';
        if (!kpiId) errors.kpiId = 'KPI is required';
        if (!datasource) errors.datasource = 'Datasource is required';
        if (!selectedAttributes || selectedAttributes.size === 0) {
            errors.attributes = 'Select at least one attribute';
        }

        return errors;
    };

    showFieldError = (field) => {
        const { errors, submitted } = this.state;
        return submitted && !!errors[field];
    };

    handleSubmit = (e) => {
        e.preventDefault();
        const errors = this.validateForm();
        if (Object.keys(errors).length > 0) {
            this.setState({ errors, submitted: true });
            return;
        }

        const { bizId, fnId, kpiId, datasource, selectedAttributes, formulaValue } = this.state;
        const { onSave, editingMapping } = this.props;
        const assets = this.getVisibleAssets();

        const mappingsArray = [];
        let index = 0;
        const stamp = Date.now();
        selectedAttributes.forEach(assetId => {
            const matchedAsset = assets.find(a => sameId(a.id, assetId));
            if (matchedAsset) {
                mappingsArray.push({
                    id: `m_gen_${stamp}_${index}`,
                    kpiId,
                    assetId: matchedAsset.id,
                    asset: matchedAsset,
                    status: MAPPING_STATUS.MANUAL,
                    enabled: editingMapping ? (editingMapping.enabled !== false) : true
                });
                index++;
            }
        });

        const originalKpiId = editingMapping
            ? (editingMapping.kpiId ?? editingMapping.kpi?.id ?? kpiId)
            : null;

        onSave({
            businessId: bizId,
            functionId: fnId,
            kpiId,
            datasource,
            mappings: mappingsArray,
            replaceKpiId: editingMapping ? originalKpiId : null,
            formulaUpdate: {
                id: kpiId,
                formula: formulaValue.trim()
            },
            envId: this.getSelectedEnvironmentId()
        });
    };

    render() {
        const { show, onClose, editingMapping, businesses, functions, kpis, assets, submitting, environments, filterEnvironment } = this.props;
        const { envId, bizId, fnId, kpiId, description, datasource, selectedDatasets, selectedAttributes, formulaValue, errors, envAssetsLoading } = this.state;

        const rawEnvironments = Array.isArray(environments) && environments.length > 0 ? environments : [
            { label: 'dev', value: '1' },
            { label: 'test', value: '2' },
            { label: 'Staging', value: '3' },
            { label: 'Production', value: '4' }
        ];

        const envOptions = rawEnvironments.map(env => {
            if (typeof env === 'string') {
                return { label: env, value: env };
            }
            return {
                label: env.label || env.env_name || env.name || String(env.value || env.id || ''),
                value: env.value !== undefined ? String(env.value) : (env.env_id !== undefined ? String(env.env_id) : (env.id !== undefined ? String(env.id) : (env.label || env.env_name || '')))
            };
        });

        const currentEnvId = envId || (filterEnvironment && filterEnvironment !== 'all' ? filterEnvironment : (envOptions[0] ? envOptions[0].value : ''));
        const selectedEnvOpt = envOptions.find(o => String(o.value) === String(currentEnvId) || String(o.label) === String(currentEnvId)) || envOptions[0] || null;
        const visibleAssets = Array.isArray(this.state.envAssets)
            ? this.state.envAssets
            : this.filterAssetsForEnvironment(assets, currentEnvId);

        // Cascade filters
        const filteredFunctions = functions.filter(
            (f) => !bizId || sameId(f.businessId, bizId)
        );
        const filteredKpis = kpis.filter(
            (k) => !fnId || sameId(k.functionId, fnId)
        );

        const selectedKpi = kpis.find((k) => sameId(k.id, kpiId));

        const findSelectValue = (options, id) => (
            id === '' || id == null
                ? null
                : options.find((o) => sameId(o.value, id)) || null
        );

        const isFormValid = bizId && fnId && kpiId && datasource && selectedAttributes.size > 0;

        const renderFieldError = (field) => (
            this.showFieldError(field) && errors[field]
                ? <div className="invalid-feedback d-block">{errors[field]}</div>
                : null
        );

        return (
            <Modal 
                show={show} 
                onHide={onClose}
                dialogClassName="modal-vertical shadow" 
                className="fixed-right model__help modal-integration" 
                size="xl"
            >
                <form onSubmit={this.handleSubmit} className="tw-flex tw-flex-col tw-h-full">
                    {/* Header */}
                    <Modal.Header closeButton>
                        <div className='tw-flex tw-items-center tw-gap-4'>
                            <div className='tw-flex tw-items-center tw-justify-center tw-p-[10px] tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/20 tw-bg-white dark:tw-bg-white/10 tw-rounded-[10px]'>
                                <Plus size={20} color="currentColor" className="tw-text-[#414651] dark:tw-text-white" />
                            </div>
                            <div>
                                <h5 className="modal-title !tw-mb-[2px] tw-font-semibold !tw-text-gray-900 dark:!tw-text-white">
                                    {editingMapping ? "Edit mapping" : "Add mapping"}
                                </h5>                                
                                <div className='tw-flex tw-items-center tw-gap-2'>
                                    <p className='tw-text-sm tw-font-medium tw-text-[#525252] dark:tw-text-gray-400 tw-m-0'>Bind a business KPI to one or more data attributes</p>
                                </div>
                            </div>
                        </div>
                    </Modal.Header>

                    {/* Body */}
                    <Modal.Body className="tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden !tw-p-0 scr">
                        <div className="qd-configure-integration-steps !tw-p-5">
                            <div className="vertical-stepper vertical-stepper-sm w-100 border-0">
                                
                                {/* STEP 1: Business KPI */}
                                <div className="vertical-stepper__heading !tw-pb-[2px]">
                                    <span className="vs-heading__circle">1</span>
                                    <div>
                                        <div className="vs-heading__title">Business KPI</div>
                                    </div>
                                </div>
                                <div className="vertical-stepper__content !tw-pt-0 !tw-pb-8 tw-space-y-4">
                                    <div className="tw-text-xs tw-text-[#535862] dark:tw-text-[#85888E] tw-font-medium tw-mt-[2px]">
                                        {"Environment > Business > Function > KPI"}
                                    </div>

                                    <div>
                                        <label>ENVIRONMENT</label>
                                        <Select
                                            id="nm-env"
                                            className="has-badge"
                                            classNamePrefix="select-control"
                                            placeholder="Select environment"
                                            options={envOptions}
                                            value={selectedEnvOpt}
                                            onChange={opt => this.onFieldChange('envId', opt ? opt.value : '')}
                                            components={envBadgeComponents}
                                            styles={customSelectStyles}
                                        />
                                    </div>

                                    <div>
                                        <label>BUSINESS</label>
                                        <Select
                                            id="nm-biz"
                                            className="has-badge"
                                            classNamePrefix="select-control"
                                            placeholder="Select business"
                                            options={businesses.map(b => ({ value: b.id, label: b.name }))}
                                            value={findSelectValue(
                                                businesses.map(b => ({ value: b.id, label: b.name })),
                                                bizId
                                            )}
                                            onChange={opt => this.onFieldChange('bizId', opt ? opt.value : '')}
                                            components={bizBadgeComponents}
                                            styles={customSelectStyles}
                                        />
                                        {renderFieldError('bizId')}
                                    </div>

                                    <div>
                                        <label>FUNCTION</label>
                                        <Select
                                            id="nm-fn"
                                            className={`has-badge${this.showFieldError('fnId') ? ' is-invalid' : ''}`}
                                            classNamePrefix="select-control"
                                            placeholder="Select function"
                                            options={filteredFunctions.map(f => ({ value: f.id, label: f.name }))}
                                            value={findSelectValue(
                                                filteredFunctions.map(f => ({ value: f.id, label: f.name })),
                                                fnId
                                            )}
                                            onChange={opt => this.onFieldChange('fnId', opt ? opt.value : '')}
                                            isDisabled={!bizId}
                                            components={fnBadgeComponents}
                                            styles={customSelectStyles}
                                        />
                                        {renderFieldError('fnId')}
                                    </div>

                                    <div>
                                        <label>KPI</label>
                                        <Select
                                            id="nm-kpi"
                                            className={`has-badge${this.showFieldError('kpiId') ? ' is-invalid' : ''}`}
                                            classNamePrefix="select-control"
                                            placeholder="Select KPI"
                                            options={filteredKpis.map(k => ({ value: k.id, label: k.name }))}
                                            value={findSelectValue(
                                                filteredKpis.map(k => ({ value: k.id, label: k.name })),
                                                kpiId
                                            )}
                                            onChange={opt => this.onFieldChange('kpiId', opt ? opt.value : '')}
                                            isDisabled={!fnId}
                                            components={kpiBadgeComponents}
                                            styles={customSelectStyles}
                                        />
                                        {renderFieldError('kpiId')}
                                    </div>

                                    <div>
                                        <label>DESCRIPTION</label>
                                        <div className="icon-input-group">
                                            <i className="!tw-top-[8px]"><FileText size={16} color="#A4A7AE" /></i>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                disabled={!!editingMapping}
                                                value={description || ''}
                                                onChange={e => this.setState({ description: e.target.value })}
                                                placeholder={selectedKpi ? (selectedKpi.description ? '' : 'No description available.') : 'Description of the selected KPI (e.g., "Total revenue across all regions").'}
                                                className="form-control tw-resize-none disabled:!tw-cursor-not-allowed"
                                            />
                                        </div>
                                    </div>

                                </div>

                                {/* STEP 2: Data Assets */}
                                <div className="vertical-stepper__heading !tw-pb-[2px]">
                                    <span className="vs-heading__circle">2</span>
                                    <div>
                                        <div className="vs-heading__title">Data assets</div>
                                    </div>
                                </div>
                                <div className="vertical-stepper__content !tw-pt-0 !tw-pb-8 tw-space-y-4">
                                    <div className="tw-text-xs tw-text-[#535862] dark:tw-text-[#85888E] tw-font-medium tw-mt-[2px]">
                                        {"Datasource > Datasets > Attributes"}
                                    </div>
                                    {envAssetsLoading ? (
                                        <div className="tw-text-center tw-py-6 tw-text-xs tw-text-gray-400">
                                            Loading datasets for the selected environment...
                                        </div>
                                    ) : (
                                    <DataAssetTreeView
                                        assets={visibleAssets}
                                        selectedDatasource={datasource}
                                        selectedDatasets={selectedDatasets}
                                        selectedAttributes={selectedAttributes}
                                        onSelectionChange={({ datasource: newDs, selectedDatasets: newDatasets, selectedAttributes: newAttributes }) => {
                                            this.setState({
                                                datasource: newDs,
                                                selectedDatasets: newDatasets,
                                                selectedAttributes: newAttributes,
                                                errors: { ...this.state.errors, datasource: '', attributes: '' }
                                            });
                                        }}
                                    />
                                    )}
                                    {renderFieldError('datasource')}
                                    {renderFieldError('attributes')}
                                </div>
                                {/* STEP 3: Formula */}
                                <div className="vertical-stepper__heading !tw-pb-[2px]">
                                    <span className="vs-heading__circle">3</span>
                                    <div>
                                        <div className="vs-heading__title">Formula</div>
                                    </div>
                                </div>
                                <div className="vertical-stepper__content !tw-pb-2 tw-space-y-4">
                                    <CodeMirrorFormulaBox
                                        value={formulaValue}
                                        onChange={val => this.setState({ formulaValue: val })}
                                        datasource={datasource}
                                        selectedDatasets={selectedDatasets}
                                        selectedAttributes={selectedAttributes}
                                        assets={assets}
                                    />
                                </div>
                            </div>
                        </div>
                    </Modal.Body>

                    {/* Footer */}
                    <div className="tw-flex tw-items-center tw-justify-between tw-py-3 tw-px-5 tw-border-0 tw-border-t tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-bg-white dark:tw-bg-white/5 tw-shrink-0">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-slate-500 dark:tw-text-[#B2B5BE]">
                            {isFormValid ? (
                                <span>
                                    Creates <b>{selectedAttributes.size}</b> mapping{selectedAttributes.size > 1 ? 's' : ''} <span className="tw-text-gray-400">·</span> marked <b className="tw-text-indigo-600 dark:tw-text-[#818CF8]">Manual</b>
                                </span>
                            ) : (
                                    <span className="tw-text-[#535862]">
                                    Pick a KPI and at least one attribute
                                </span>
                            )}
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <button 
                                type="button" 
                                onClick={onClose}
                                className="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-white tw-bg-white dark:tw-bg-[#2B2B40] tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg hover:tw-bg-gray-50 dark:hover:tw-bg-[#3A3A55] tw-transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                disabled={!isFormValid || submitting}
                                className="btn btn-primary"
                            >
                                {submitting
                                    ? 'Saving...'
                                    : (editingMapping ? 'Save Changes' : '+ Add mapping')}
                            </button>
                        </div>
                    </div>
                </form>
            </Modal>
        );
    }
}

export default AddMappingModal;
